import React, { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useSections, isSectionOn } from "@/hooks/useSections";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Package as PkgIcon } from "lucide-react";

export default function QuoteRequest() {
  const { t, lang } = useI18n();
  const sections = useSections();
  const [params] = useSearchParams();
  const selectedPackage = params.get("package") || "";
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    // Pre-fill service with the picked package name so the existing
    // Submission.service column carries the association through to Admin.
    service: selectedPackage || "governance",
    budget: "", message: selectedPackage ? `Package inquiry: ${selectedPackage}` : "",
    consent: true,
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (selectedPackage) {
      setForm((f) => ({
        ...f, service: selectedPackage,
        message: f.message || `Package inquiry: ${selectedPackage}`,
      }));
    }
  }, [selectedPackage]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quote", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  if (!isSectionOn(sections, "quote")) return (
    <div className="container-x py-24 text-navy/60" data-testid="quote-off">
      {lang === "ar" ? "هذا القسم غير متاح حالياً." : "This section is currently unavailable."}
    </div>
  );

  if (done) {
    return (
      <div className="container-x py-24" data-testid="quote-thank-you">
        <div className="card-elev p-12 max-w-2xl">
          <CheckCircle2 size={40} className="text-gold" />
          <h1 className="mt-4 text-3xl md:text-4xl text-navy">{t("common.thankYou")}</h1>
          <span className="divider-gold" />
          <p className="text-navy/75">{lang === "ar" ? "سنراجع طلبك ونعود إليك خلال 48 ساعة." : "We will review and respond within 48 hours."}</p>
        </div>
      </div>
    );
  }

  const services = [
    { v: "governance", ar: "الحوكمة المؤسسية", en: "Corporate Governance" },
    { v: "compliance", ar: "الامتثال التنظيمي", en: "Regulatory Compliance" },
    { v: "risk", ar: "إدارة المخاطر", en: "Risk Management" },
    { v: "training", ar: "التدريب التنفيذي", en: "Executive Training" },
    { v: "other", ar: "أخرى", en: "Other" },
  ];

  return (
    <div data-testid="quote-page" className="container-x py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
      <div className="md:col-span-2">
        <span className="eyebrow">{t("nav.quote")}</span>
        <h1 className="mt-4 text-4xl md:text-5xl text-navy">
          {lang === "ar" ? "احصل على عرض سعر مخصّص" : "Request a Bespoke Quote"}
        </h1>
        <span className="divider-gold" />
        <p className="text-navy/70">
          {lang === "ar"
            ? "صمّم مع فريقنا برنامجاً مفصّلاً يلائم مؤسستك."
            : "Design a tailored programme with our senior partners."}
        </p>
        {selectedPackage && (
          <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-gold/15 border border-gold/40 rounded-sm text-navy" data-testid="quote-selected-package">
            <PkgIcon size={14} className="text-gold" />
            <span className="text-sm">
              {lang === "ar" ? "الباقة المختارة: " : "Selected package: "}
              <b>{selectedPackage}</b>
            </span>
          </div>
        )}
      </div>
      <form onSubmit={submit} className="md:col-span-3 card-elev p-8 md:p-10 space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <input required data-testid="q-name" placeholder={t("common.name")} className="field-box" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input required type="email" data-testid="q-email" placeholder={t("common.email")} className="field-box" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input required data-testid="q-company" placeholder={t("common.company")} className="field-box" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <input data-testid="q-phone" placeholder={t("common.phone")} className="field-box" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </div>
        <select data-testid="q-service" className="field-box" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
          {services.map((s) => (<option key={s.v} value={s.v}>{lang === "ar" ? s.ar : s.en}</option>))}
        </select>
        <input data-testid="q-budget" placeholder={t("common.budget")} className="field-box" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} />
        <textarea required data-testid="q-message" rows={5} placeholder={t("common.message")} className="field-box" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
        <label className="flex items-start gap-3 text-sm text-navy/75">
          <input data-testid="q-consent" type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 accent-gold-500" />
          <span>{t("common.consent")}</span>
        </label>
        <button data-testid="q-submit" type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-40">
          {loading ? "..." : t("common.send")}
        </button>
      </form>
    </div>
  );
}
